import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkle, Crown } from "lucide-react";
import MagneticButton from "@/components/site/MagneticButton";
import { cn } from "@/lib/utils";

/**
 * PackageCard — premium branding card with:
 *   • cursor-tracking glow (pg-glow-card sets --mx/--my)
 *   • hover lift + ambient lighting
 *   • animated gold price gradient
 *   • elevated flagship (tier 6 "Signature Brand Ecosystem") with glass-morphism +
 *     dual teal/gold cinematic glow + premium hierarchy
 */
export const PackageCard = ({ pkg, index = 0, onChoose, variant = "logo", testIdPrefix = "package" }) => {
    const tier = pkg.tier || 1;
    // Accent style takes precedence (set from CMS); fall back to tier-based logic.
    const accentStyle = pkg.accent_style || (tier >= 6 ? "apex" : tier >= 5 ? "flagship" : "default");
    const isFlagship = accentStyle === "flagship" || accentStyle === "apex";
    const isApex = accentStyle === "apex";
    const isSpaceWhite = accentStyle === "flagship" && !isApex; // "Most Popular" tier
    const priceLabel = pkg.price_label || pkg.priceLabel || "";
    const priceNote = pkg.price_note || pkg.priceNote || "";
    const cardRef = useRef(null);
    const [inView, setInView] = useState(false);

    // Soft clockwise edge-light colour, tuned per package identity.
    const ringColor = isApex
        ? "#F3B6FF"            // galactic pink/purple
        : isSpaceWhite
            ? "#9FB0C8"        // space-white / soft steel
            : variant === "branding"
                ? "#8FE9E0"    // soft aqua
                : "#F7C67C";   // gold (logo default)

    // On touch devices, light the ring when this pack scrolls into view.
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!window.matchMedia("(pointer: coarse)").matches) return;
        const el = cardRef.current;
        if (!el || !("IntersectionObserver" in window)) return;
        const io = new IntersectionObserver(
            ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio > 0.55),
            { threshold: [0, 0.55, 1], rootMargin: "-12% 0px -12% 0px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const onMouseMove = useCallback((e) => {
        const el = cardRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        el.style.setProperty("--mx", `${x}%`);
        el.style.setProperty("--my", `${y}%`);
    }, []);

    const onTouchMove = useCallback((e) => {
        const el = cardRef.current;
        if (!el) return;
        const t = e.touches?.[0];
        if (!t) return;
        const rect = el.getBoundingClientRect();
        const x = ((t.clientX - rect.left) / rect.width) * 100;
        const y = ((t.clientY - rect.top) / rect.height) * 100;
        el.style.setProperty("--mx", `${x}%`);
        el.style.setProperty("--my", `${y}%`);
    }, []);

    return (
        <motion.article
            ref={cardRef}
            onMouseMove={onMouseMove}
            onTouchMove={onTouchMove}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.08 * index, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
                "pg-pack-card relative flex flex-col rounded-3xl border p-7 sm:p-8",
                isApex ? "pg-flagship-glass" : "pg-glow-card",
                isSpaceWhite && "pg-pack-spacewhite",
                isFlagship && !isApex && !isSpaceWhite && "pg-card-flagship"
            )}
            data-testid={`${testIdPrefix}-card`}
            data-package-id={pkg.id}
            data-tier={tier}
            data-inview={inView ? "true" : "false"}
            style={{
                "--ring-color": ringColor,
                ...(isApex || isSpaceWhite
                    ? {}
                    : { background: "var(--pg-surface)", borderColor: "var(--pg-border)" }),
            }}
        >
            {/* Per-package clockwise edge-light (hover on desktop, in-view on touch) */}
            <span className="pg-pack-ring" aria-hidden="true" />
            {/* --- Flagship (apex) prestige ornaments --- */}
            {isApex && (
                <>
                    {/* Slow drifting cosmic starfield */}
                    <span className="pg-flagship-stars" aria-hidden />
                    {/* Crown-ish top ornament (3 pulsing gold pins) */}
                    <span className="pg-flagship-ornament" aria-hidden>
                        <span />
                        <span />
                        <span />
                    </span>
                    {/* Floating sparkle particles inside the card body */}
                    <span className="pg-flagship-particle" style={{ top: "22%", left: "8%", animationDelay: "0s" }} aria-hidden />
                    <span className="pg-flagship-particle" style={{ top: "45%", right: "10%", animationDelay: "-1.8s" }} aria-hidden />
                    <span className="pg-flagship-particle" style={{ top: "72%", left: "18%", animationDelay: "-3.2s" }} aria-hidden />
                    <span className="pg-flagship-particle" style={{ top: "58%", right: "24%", animationDelay: "-4.6s", background: "rgba(245, 182, 255, 0.9)" }} aria-hidden />
                </>
            )}
            {pkg.ribbon && (
                <span
                    className={cn(
                        "absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] font-semibold",
                        isApex ? "px-3 py-1" : "px-3 py-1"
                    )}
                    style={
                        isApex
                            ? {
                                  background:
                                      "linear-gradient(120deg, color-mix(in srgb, var(--pg-accent) 92%, transparent) 0%, #FCEACB 60%, color-mix(in srgb, var(--pg-accent) 92%, transparent) 100%)",
                                  color: "#071112",
                                  boxShadow: "0 0 24px rgba(var(--pg-accent-rgb), 0.45)",
                              }
                            : { background: "var(--pg-accent)", color: "#071112" }
                    }
                    data-testid={`${testIdPrefix}-ribbon`}
                >
                    {isApex ? <Crown className="h-3 w-3" strokeWidth={2} /> : isFlagship && <Sparkle className="h-3 w-3" strokeWidth={2} />}
                    {pkg.ribbon}
                </span>
            )}

            <div className="flex items-center gap-3">
                <span
                    className="font-mono text-[11px] uppercase tracking-[0.22em]"
                    style={{ color: isApex ? "rgba(252,234,203,0.85)" : "var(--pg-fg-muted)" }}
                >
                    {variant === "logo" ? "Logo Pack" : "Branding"}
                </span>
                <span
                    className="h-px w-8"
                    style={{ background: isApex ? "rgba(252,234,203,0.30)" : "var(--pg-border-strong)" }}
                />
            </div>

            {/* Prestige band — apex only */}
            {isApex && (
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-4 inline-flex"
                >
                    <span className="pg-flagship-band" data-testid={`${testIdPrefix}-prestige-band`}>
                        <Crown className="h-3 w-3" strokeWidth={2} />
                        The Signature Experience
                    </span>
                </motion.div>
            )}

            <h3
                className={cn(
                    "font-display leading-[1.05]",
                    isApex ? "mt-5 text-3xl sm:text-[36px]" : "mt-3 text-2xl sm:text-[28px]"
                )}
                style={{ color: isApex ? "#FFFFFF" : "var(--pg-fg)" }}
                data-testid="package-name"
            >
                {pkg.name}
            </h3>

            <div className="mt-5 flex items-end gap-2">
                {priceNote && (
                    <span
                        className="font-mono pb-1 text-[11px] uppercase tracking-[0.18em]"
                        style={{ color: isApex ? "rgba(252,234,203,0.75)" : "var(--pg-fg-muted)" }}
                    >
                        {priceNote}
                    </span>
                )}
                <span
                    className={cn(
                        "pg-price-num pg-price-gold",
                        isApex ? "text-5xl sm:text-[62px]" : "text-4xl sm:text-5xl"
                    )}
                    data-testid="package-price"
                >
                    {priceLabel}
                </span>
            </div>

            <p
                className="mt-4 text-sm leading-relaxed"
                style={{ color: isApex ? "rgba(252,234,203,0.78)" : "var(--pg-fg-muted)" }}
            >
                {pkg.description}
            </p>

            <div
                className="my-6 h-px w-full"
                style={{
                    background: isApex
                        ? "linear-gradient(90deg, transparent 0%, rgba(252,234,203,0.30) 50%, transparent 100%)"
                        : "linear-gradient(90deg, transparent 0%, var(--pg-border) 40%, var(--pg-border) 60%, transparent 100%)",
                }}
            />

            <div className="flex flex-1 flex-wrap content-start gap-2" data-testid={`${testIdPrefix}-features`}>
                {pkg.features.map((feat, fi) => {
                    const count = pkg.features.length;
                    const step = count > 1 ? Math.min(2.6 / (count - 1), 0.4) : 0;
                    return (
                        <motion.span
                            key={feat}
                            initial={{ opacity: 0, y: 8, scale: 0.94 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.42, delay: fi * step, ease: [0.16, 1, 0.3, 1] }}
                            className={cn("pg-pack-chip", isApex && "pg-pack-chip--apex")}
                        >
                            <span className="pg-pack-chip__tick" aria-hidden>
                                <Check className="h-3 w-3" strokeWidth={2.6} />
                            </span>
                            {feat}
                        </motion.span>
                    );
                })}
            </div>

            <MagneticButton
                type="button"
                onClick={() => onChoose?.(pkg)}
                strength={isApex ? 6 : 14}
                className={cn(
                    "mt-8 w-full",
                    isApex ? "" : isFlagship ? "pg-btn-primary" : "pg-btn-ghost"
                )}
                style={
                    isApex
                        ? {
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.5rem",
                              padding: "0.95rem 1.5rem",
                              borderRadius: 999,
                              fontWeight: 500,
                              fontSize: "0.95rem",
                              background: "linear-gradient(120deg, var(--pg-accent) 0%, #FCEACB 50%, var(--pg-accent) 100%)",
                              backgroundSize: "200% 100%",
                              color: "#071112",
                              border: "1px solid rgba(var(--pg-accent-rgb), 0.6)",
                              boxShadow:
                                  "0 18px 44px rgba(var(--pg-accent-rgb), 0.35), 0 0 30px rgba(47,227,208,0.18)",
                          }
                        : undefined
                }
                data-testid={`${testIdPrefix}-cta`}
            >
                {pkg.cta}
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.6} />
            </MagneticButton>
        </motion.article>
    );
};

export default PackageCard;
